<script setup>
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import AuthLayout from "@/components/layout/AuthLayout.vue";
import AppCard from "@/components/common/AppCard.vue";
import AppInput from "@/components/common/AppInput.vue";

const router = useRouter();
const auth = useAuthStore();

const form = reactive({
  username: "",
  password: "",
});

const rememberMe = ref(true);
const showPassword = ref(false);
const submitted = ref(false);

const fillMockUser = (username, password) => {
  form.username = username;
  form.password = password;
  submitted.value = false;
};

const handleLogin = async () => {
  submitted.value = true;

  if (!form.username || !form.password) return;

  const result = await auth.login(form);

  if (result.success) {
    router.push("/dashboard");
  }
};

const getMockUserBtnClass = (role) => {
  switch (role) {
    case "admin":
      return "btn-outline-primary border-primary-subtle";
    case "finance":
      return "btn-outline-success border-success-subtle";
    case "user":
      return "btn-outline-dark border-dark-subtle";
    default:
      return "btn-outline-secondary";
  }
};

const getMockUserIconClass = (role) => {
  switch (role) {
    case "admin":
      return "bi-gear-fill";
    case "finance":
      return "bi-currency-dollar";
    case "user":
      return "bi-person-fill";
    default:
      return "bi-person-x-fill";
  }
};
</script>
<template>
  <AuthLayout>
    <div class="mx-auto" style="max-width: 460px">
      <AppCard>
        <div
          class="d-flex flex-column flex-sm-row align-items-center gap-3 mb-4 text-center text-sm-start"
        >
          <div class="login-logo flex-shrink-0">
            <i class="bi bi-buildings"></i>
          </div>

          <div>
            <h2 class="fw-bold mb-1">เข้าสู่ระบบ</h2>
            <p class="text-muted mb-0">กรุณากรอกข้อมูลเพื่อเข้าใช้งานระบบ</p>
          </div>
        </div>

        <div
          v-if="auth.error"
          class="alert alert-danger d-flex align-items-center rounded-3"
          role="alert"
        >
          <i class="bi bi-exclamation-circle me-2"></i>
          <div>{{ auth.error }}</div>
        </div>

        <form @submit.prevent="handleLogin">
          <AppInput
            v-model="form.username"
            label="ชื่อผู้ใช้"
            placeholder="กรอกชื่อผู้ใช้"
            icon="bi-person-fill"
            :error="submitted && !form.username ? 'กรุณากรอกชื่อผู้ใช้' : ''"
          />

          <AppInput
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            label="รหัสผ่าน"
            placeholder="กรอกรหัสผ่าน"
            icon="bi-shield-lock-fill"
            :passwordToggle="true"
            :showPassword="showPassword"
            :error="submitted && !form.password ? 'กรุณากรอกรหัสผ่าน' : ''"
            @toggle-password="showPassword = !showPassword"
          />

          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="rememberMe"
                v-model="rememberMe"
              />
              <label class="form-check-label text-muted" for="rememberMe">
                จดจำการเข้าสู่ระบบ
              </label>
            </div>

            <a href="#" class="text-decoration-none small"> ลืมรหัสผ่าน? </a>
          </div>

          <button
            type="submit"
            class="btn btn-primary w-100 rounded-3 py-auto fw-semibold align-items-center"
            :disabled="auth.loading"
          >
            <span
              v-if="auth.loading"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            <i v-else class="bi bi-box-arrow-in-right me-1"></i
            >{{ auth.loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ" }}
          </button>
        </form>

        <div class="bg-light rounded-3 p-3">
          <div class="fw-bold text-center text-secondary">
            <i class="bi bi-person-circle me-1"></i>
            Mock User สำหรับทดสอบ คลิกเลย
          </div>

          <div class="d-grid gap-1 d-md-block">
            <button
              v-for="user in auth.users"
              :key="user.id"
              type="button"
              class="btn btn-sm text-center m-1 shadow-sm"
              :class="getMockUserBtnClass(user.role)"
              @click="fillMockUser(user.username, user.password)"
            >
              <i class="bi me-1" :class="getMockUserIconClass(user.role)"></i
              >{{ user.name }}
            </button>
          </div>
        </div>
      </AppCard>
    </div>
  </AuthLayout>
</template>

<style scoped>
.login-logo {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: #0d6efd;
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.12),
    rgba(13, 202, 240, 0.12)
  );
}
</style>
